import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ClaimMetaJson } from '../platform/api';
import { useEmbark } from '../platform/store';
import { WakeProgress, WorldStatusBadge, useWorlds } from '../worlds';
import type { WorldInfoJson } from '../platform/api';
import styles from './embark.module.css';
import worldStyles from '../worlds/worlds.module.css';

function CreateWorldDialog({
  claim,
  onClose,
  onCreated,
}: {
  claim: ClaimMetaJson;
  onClose: () => void;
  onCreated: (world: WorldInfoJson) => void;
}): JSX.Element {
  const [name, setName] = useState(claim.name);
  const [visibility, setVisibility] = useState('Public');
  const [maxPlayers, setMaxPlayers] = useState(20);
  const [creating, setCreating] = useState(false);
  const createAndPoll = useWorlds((s) => s.createAndPoll);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    const world = await createAndPoll(claim.claim_id, name, visibility, maxPlayers);
    if (world) onCreated(world);
    else setCreating(false);
  }, [claim.claim_id, name, visibility, maxPlayers, createAndPoll, onCreated]);

  return (
    <div className={worldStyles.dialogOverlay} onClick={onClose}>
      <div className={worldStyles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={worldStyles.dialogTitle}>Start World</div>
        <div className={worldStyles.dialogField}>
          <label className={worldStyles.dialogLabel}>World Name</label>
          <input
            className={worldStyles.dialogInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={creating}
          />
        </div>
        <div className={worldStyles.dialogField}>
          <label className={worldStyles.dialogLabel}>Visibility</label>
          <select
            className={worldStyles.dialogSelect}
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            disabled={creating}
          >
            <option value="Public">Public</option>
            <option value="FriendsOnly">Friends Only</option>
            <option value="Private">Private</option>
          </select>
        </div>
        <div className={worldStyles.dialogField}>
          <label className={worldStyles.dialogLabel}>Max Players</label>
          <input
            className={worldStyles.dialogInput}
            type="number"
            min={1}
            max={100}
            value={maxPlayers}
            onChange={(e) => setMaxPlayers(Number(e.target.value))}
            disabled={creating}
          />
        </div>
        {creating && <WakeProgress status="Provisioning" />}
        <div className={worldStyles.dialogActions}>
          <button
            className={styles.primaryBtn}
            onClick={() => void handleCreate()}
            disabled={creating || !name.trim()}
          >
            {creating ? 'Creating...' : 'Create & Play'}
          </button>
          <button className={styles.secondaryBtn} onClick={onClose} disabled={creating}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function MyWorlds({
  onCreateNew,
  onExpand,
}: {
  onCreateNew: () => void;
  onExpand: (claim: ClaimMetaJson) => void;
}): JSX.Element {
  const myClaims = useEmbark((s) => s.myClaims);
  const fetchMyClaims = useEmbark((s) => s.fetchMyClaims);
  const loadWorlds = useWorlds((s) => s.loadWorlds);
  const worlds = useWorlds((s) => s.worlds);
  const wakeAndPoll = useWorlds((s) => s.wakeAndPoll);
  const pollingWorldId = useWorlds((s) => s.pollingWorldId);
  const pollingStatus = useWorlds((s) => s.pollingStatus);
  const navigate = useNavigate();
  const [dialogClaim, setDialogClaim] = useState<ClaimMetaJson | null>(null);

  useEffect(() => {
    void fetchMyClaims();
    void loadWorlds();
  }, [fetchMyClaims, loadWorlds]);

  const getWorldForClaim = useCallback(
    (claimId: number): WorldInfoJson | undefined =>
      worlds.find((w) => w.claim_id === claimId),
    [worlds],
  );

  const handlePlay = useCallback(
    async (claim: ClaimMetaJson) => {
      const existing = getWorldForClaim(claim.claim_id);

      if (!existing) {
        setDialogClaim(claim);
        return;
      }

      if (existing.status === 'Active') {
        navigate(`/play/${claim.claim_id}`, { state: { worldId: existing.world_id } });
        return;
      }

      if (existing.status === 'Hibernating') {
        const world = await wakeAndPoll(existing.world_id);
        if (world) {
          navigate(`/play/${claim.claim_id}`, { state: { worldId: world.world_id } });
        }
        return;
      }
    },
    [getWorldForClaim, navigate, wakeAndPoll],
  );

  const handleWorldCreated = useCallback(
    (world: WorldInfoJson) => {
      setDialogClaim(null);
      if (world.claim_id != null) {
        navigate(`/play/${world.claim_id}`, { state: { worldId: world.world_id } });
      }
    },
    [navigate],
  );

  return (
    <>
      <div className={styles.worldsBody}>
        {myClaims.length === 0 ? (
          <div className={styles.worldsEmpty}>
            <div className={styles.worldsEmptyTitle}>
              You don't have a world yet. Create one!
            </div>
            <p>Pick a spot on the map and claim your land.</p>
            <button className={styles.primaryBtn} onClick={onCreateNew}>
              Create New World
            </button>
          </div>
        ) : (
          <>
            <div className={styles.worldsGrid}>
              {myClaims.map((claim) => {
                const world = getWorldForClaim(claim.claim_id);
                const isPolling = world && pollingWorldId === world.world_id;

                return (
                  <div key={claim.claim_id} className={styles.worldCard}>
                    <div
                      onClick={() => void handlePlay(claim)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className={styles.worldCardName}>{claim.name}</div>
                      <div className={styles.worldCardMeta}>
                        {claim.mega_world} &middot; {claim.block_count} block
                        {claim.block_count !== 1 ? 's' : ''}
                        {world && (
                          <>
                            {' '}&middot;{' '}
                            <WorldStatusBadge status={world.status} />
                          </>
                        )}
                      </div>
                    </div>
                    {isPolling && pollingStatus && (
                      <WakeProgress status={pollingStatus} />
                    )}
                    <div className={styles.worldCardActions}>
                      <button
                        className={styles.primaryBtn}
                        style={{ padding: '6px 14px', fontSize: 12 }}
                        onClick={() => void handlePlay(claim)}
                        disabled={!!pollingWorldId}
                      >
                        {!world
                          ? 'Play'
                          : world.status === 'Active'
                            ? 'Connect'
                            : world.status === 'Hibernating'
                              ? 'Wake & Play'
                              : 'Play'}
                      </button>
                      <button
                        className={styles.secondaryBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          onExpand(claim);
                        }}
                      >
                        Expand
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <button className={styles.primaryBtn} onClick={onCreateNew}>
              Create New World
            </button>
          </>
        )}
      </div>

      {dialogClaim && (
        <CreateWorldDialog
          claim={dialogClaim}
          onClose={() => setDialogClaim(null)}
          onCreated={handleWorldCreated}
        />
      )}
    </>
  );
}
